import { api } from "./api";
import { Plan } from "@dto/Plan";
import { PlanItem, PlanItemMinimal } from "@dto/PlanItem";
import { convertToCamel } from "@lib/convertToCamel";
import { convertToSnake } from "@lib/convertToSnake";

export const plansApi = api.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    createPlan: build.mutation<Plan, Partial<Plan>>({
      query: (plan) => {
        const body = convertToSnake(plan);

        return {
          url: `/plans/create_or_merge`,
          body: body,
          method: "POST",
        };
      },
      transformResponse: (responseData) => {
        return convertToCamel(responseData) as Plan;
      },
      invalidatesTags: (result) =>
        result?.id ? [{ type: "Plan", id: result.id }] : [],
    }),
    getPlan: build.query<Plan & { planItems: PlanItem[] }, number>({
      query: (planId) => {
        return { url: `/plans/${planId}` };
      },
      transformResponse: (responseData) => {
        return convertToCamel(responseData) as Plan & {
          planItems: PlanItem[];
        };
      },
      providesTags: (_plan, _err, id) => [{ type: "Plan", id }],
    }),
    updatePlan: build.mutation<
      Plan & {
        planItems: PlanItem[];
      },
      Partial<Plan> & {
        id: number;
        planItemsAttributes?: {
          id: number;
          dayIndex?: number;
          position?: number;
        }[];
      }
    >({
      query: (plan) => {
        const body = convertToSnake(plan);

        return {
          url: `/plans/${plan.id}`,
          body: body,
          method: "PUT",
        };
      },
      transformResponse: (responseData) => {
        const result = convertToCamel(responseData) as Plan & {
          planItems: PlanItem[];
        };

        if (result.dayDuration) {
          // Workaround for backend bug, it doesn't return updated dayIndex value which is needed
          // when a plan's dates are shortened such that a plan item falls outside the new bounds.
          result.planItems.forEach((planItem) => {
            if (
              planItem.dayIndex !== null &&
              planItem.dayIndex > result.dayDuration!
            ) {
              planItem.dayIndex = null;
            }
          });
        }

        return result;
      },
      extraOptions: {
        maxRetries: 0,
      },
      invalidatesTags: (result, error, { id }) => [{ type: "Plan", id: id }],
    }),
    addPlanItem: build.mutation<
      PlanItem,
      {
        planItem: {
          plannableId: number;
          plannableType: string;
          dayIndex: number | null;
          position?: number;
        };
        planId: number;
      }
    >({
      query: ({ planId, planItem }) => {
        return {
          url: `/plans/${planId}/plan_items`,
          body: convertToSnake(planItem),
          method: "POST",
        };
      },
      transformResponse: (responseData) => {
        return convertToCamel(responseData) as PlanItem;
      },
      invalidatesTags: (result, error, { planId }) => [
        { type: "Plan", id: planId },
      ],
    }),
    addTour: build.mutation<
      PlanItem[],
      {
        tourId: number;
        planId: number;
        dayIndex: number | null;
      }
    >({
      query: ({ planId, tourId, dayIndex }) => {
        const query = new URLSearchParams();
        query.append("tour_id", String(tourId));
        if (dayIndex !== null) {
          query.append("day_index", String(dayIndex));
        }

        return {
          url: `/plans/${planId}/add_tour_to_plan?${query.toString()}`,
          method: "POST",
        };
      },
      transformResponse: (responseData) => {
        const result = convertToCamel(responseData) as PlanItem[];
        return result;
      },
      invalidatesTags: (result, error, { planId }) => [
        { type: "Plan", id: planId },
      ],
    }),
    updatePlanItem: build.mutation<
      PlanItemMinimal,
      {
        planItem: Partial<PlanItem> & { id: number };
        planId: number;
      }
    >({
      query: ({ planId, planItem }) => {
        return {
          url: `/plans/${planId}/plan_items/${planItem.id}`,
          body: convertToSnake(planItem),
          method: "PUT",
        };
      },
      transformResponse: (responseData) => {
        return convertToCamel(responseData) as PlanItemMinimal;
      },
      invalidatesTags: (result, error, { planId }) => [
        { type: "Plan", id: planId },
      ],
    }),
    removePlanItem: build.mutation<
      void,
      {
        planItemId: number;
        planId: number;
      }
    >({
      query: ({ planId, planItemId }) => {
        return {
          url: `/plans/${planId}/plan_items/${planItemId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: (result, error, { planId }) => [
        { type: "Plan", id: planId },
      ],
    }),
    removePlanItems: build.mutation<
      void,
      {
        planItemIds: number[];
        planId: number;
      }
    >({
      query: ({ planId, planItemIds }) => {
        const query = new URLSearchParams();
        planItemIds.forEach((planItemId) => {
          query.append("ids[]", String(planItemId));
        });

        return {
          url: `/plans/${planId}/plan_items/?${query.toString()}`,
          method: "DELETE",
        };
      },
      invalidatesTags: (result, error, { planId }) => [
        { type: "Plan", id: planId },
      ],
    }),
    removeTour: build.mutation<
      PlanItem[],
      {
        planId: number;
        tourId: number;
      }
    >({
      query: ({ planId, tourId }) => {
        return {
          url: `/plans/${planId}/remove_tour_from_plan`,
          body: convertToSnake({ tourId: tourId }),
          method: "DELETE",
        };
      },
      transformResponse: (responseData) => {
        return convertToCamel(responseData) as PlanItem[];
      },
      invalidatesTags: (result, error, { planId }) => [
        { type: "Plan", id: planId },
      ],
    }),
    updateDayPlanItems: build.mutation<
      void,
      {
        planItemIds: number[];
        planId: number;
        dayIndex: number;
      }
    >({
      query: ({ planId, planItemIds, dayIndex }) => {
        return {
          url: `/plans/${planId}/days/${dayIndex}`,
          body: convertToSnake({ planItemIds }),
          method: "PUT",
        };
      },
      invalidatesTags: (result, error, { planId }) => [
        { type: "Plan", id: planId },
      ],
    }),
  }),
});

export const {
  useUpdatePlanMutation,
  useGetPlanQuery,
  useAddPlanItemMutation,
  useUpdatePlanItemMutation,
  useRemovePlanItemMutation,
  useRemovePlanItemsMutation,
  useRemoveTourMutation,
  useUpdateDayPlanItemsMutation,
  useCreatePlanMutation,
  useAddTourMutation,
} = plansApi;

export const {
  endpoints: { updatePlan, addPlanItem, getPlan, addTour },
} = plansApi;
